import React, { useState, useEffect } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { FaArrowRight } from 'react-icons/fa';

// Type-cast icons for React compatibility
const FaArrowRightComponent = FaArrowRight as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPlusComponent = FiPlus as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for field configuration
interface FieldConfig {
  name: string; // Field name
  inputType: string; // Input type (e.g., Text, Dropdown)
  filter: boolean; // Whether the field is filterable
  isMandatory?: boolean; // Whether the field is required
  isNew?: boolean; // Whether the field is newly added
}

// Props interface for the EditTableFormatModal component
interface EditTableFormatModalProps {
  isOpen: boolean; // Controls modal visibility
  onClose: () => void; // Callback to close the modal
  fields: string[]; // Current fields in the table
  initialFields: string[]; // Initial fields for comparison
  onSave: (updatedConfigs: FieldConfig[]) => void; // Callback to save updated configurations
}

// Component for editing the table format (columns and their properties)
const EditTableFormatModal: React.FC<EditTableFormatModalProps> = ({
  isOpen,
  onClose,
  fields,
  initialFields,
  onSave,
}) => {
  // State for field configurations, new field name, selected field, and change tracking
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([]);
  const [newFieldName, setNewFieldName] = useState('');
  // const [selectedField, setSelectedField] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize field configurations when modal opens
  useEffect(() => {
    if (isOpen) {
      // Convert field names to FieldConfig objects, preserving existing filter states
      const currentConfigs = fields.map((field) => {
        const existingConfig = fieldConfigs.find((fc) => fc.name === field);
        if (existingConfig) {
          return existingConfig;
        }
        return {
          name: field,
          inputType: getDefaultInputType(field),
          filter: getDefaultFilter(field),
          isMandatory: field === 'Name & Email',
        };
      });
      setFieldConfigs(currentConfigs);
      setNewFieldName('');
      // setSelectedField('');
      setHasChanges(false);
    }
  }, [isOpen, fields]);

  // Helper function to determine default input type for a field
  const getDefaultInputType = (field: string): string => {
    switch (field) {
      case 'DOB':
        return 'Calendar';
      case 'Gender':
      case 'Tag':
        return 'Dropdown';
      case 'Contact':
      case 'Engage Score':
        return 'Number';
      default:
        return 'Text';
    }
  };

  // Helper function to determine default filter state for a field
  const getDefaultFilter = (field: string): boolean => {
    return field !== 'DOB';
  };

  // Checks if there are meaningful changes in field configurations
  const checkForChanges = (configs: FieldConfig[]) => {
    const initialConfigs = fields.map((field) => ({
      name: field,
      inputType: getDefaultInputType(field),
      filter: getDefaultFilter(field),
      isMandatory: field === 'Name & Email',
    }));

    // Filter out configs with empty names (unconfirmed new fields)
    const meaningfulConfigs = configs.filter(
      (config) => config.name.trim() !== ''
    );

    // Compare configurations and check for removed fields
    const configsChanged =
      JSON.stringify(meaningfulConfigs) !== JSON.stringify(initialConfigs);
    const removedFields = initialFields.some(
      (field) => !configs.some((config) => config.name === field)
    );

    return configsChanged || removedFields;
  };

  // Update hasChanges state when fieldConfigs change
  useEffect(() => {
    if (isOpen) {
      setHasChanges(checkForChanges(fieldConfigs));
    }
  }, [fieldConfigs, isOpen]);

  // Adds a new column configuration
  const addNewColumn = () => {
    const nameEmailIndex = fieldConfigs.findIndex(
      (config) => config.name === 'Name & Email'
    );
    const newConfigs = [...fieldConfigs];
    newConfigs.splice(nameEmailIndex + 1, 0, {
      name: '',
      inputType: 'Text',
      filter: true,
      isNew: true,
    });
    setFieldConfigs(newConfigs);
    // setSelectedField('');
  };

  // Updates a specific field configuration
  const updateFieldConfig = (
    index: number,
    key: keyof FieldConfig,
    value: string | boolean
  ) => {
    const newConfigs = [...fieldConfigs];
    newConfigs[index] = { ...newConfigs[index], [key]: value };
    setFieldConfigs(newConfigs);
  };

  // Handles adding a new field name
  const handleAddNewField = (index: number) => {
    if (newFieldName.trim()) {
      const newConfigs = [...fieldConfigs];
      newConfigs[index] = {
        name: newFieldName.trim(),
        inputType: 'Text',
        filter: true,
        isNew: false,
      };
      setFieldConfigs(newConfigs);
      setNewFieldName('');
      // setSelectedField(newFieldName.trim());
      const dropdown = document.getElementById(`dropdown-${index}`);
      if (dropdown) dropdown.style.display = 'none';
    }
  };

  // Saves updated field configurations
  const handleSave = () => {
    const updatedConfigs = fieldConfigs.filter((config) => config.name);
    const removedFields = initialFields.filter(
      (field) => !fieldConfigs.some((config) => config.name === field)
    );
    if (removedFields.length > 0) {
      toast.success('Fields successfully removed from table');
    }
    if (fieldConfigs.some((config) => !initialFields.includes(config.name))) {
      toast.success('New field added to table');
    }
    onSave(updatedConfigs);
    onClose();
  };

  // Closes the modal without saving
  const handleClose = () => {
    onClose();
  };

  // Identifies fields that were removed
  const deletedFields = initialFields.filter(
    (field) =>
      field !== 'Name & Email' &&
      !fieldConfigs.some((config) => config.name === field)
  );

  // Return null if modal is not open
  if (!isOpen) return null;

  // Renders the modal for editing table format
  return (
    // Modal overlay with semi-transparent background
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      {/* Modal content container */}
      <div className='bg-white rounded-lg p-6 w-[900px]'>
        {/* Modal header */}
        <div className='flex justify-between items-center mb-1'>
          <h2 className='text-lg font-semibold'>Edit Table format</h2>
          <button
            onClick={handleClose}
            className='text-gray-500 hover:text-gray-700'
          >
            <FiXComponent size={20} />
          </button>
        </div>
        {/* Modal description */}
        <p className='text-sm text-gray-500 mb-4'>
          Select columns and type to be present in the table.
        </p>
        {/* Button to add new column */}
        <button
          onClick={addNewColumn}
          className='mb-4 flex items-center text-teal-600 hover:text-teal-700'
        >
          <FiPlusComponent size={16} className='mr-2' /> Add new column
        </button>
        {/* Table header for fields, input type, and filter */}
        <div className='flex items-center text-[#1F2937] text-[18px] mb-2'>
          <div className='w-1/3 p-2 mr-2 font-semibold'>Fields</div>
          <div className='w-1/3 p-2 pl-0 mr-2 font-semibold'>Input Type</div>
          <div className='w-1/6 p-2 pl-0 mr-2 -ml-[1%] font-semibold'>
            Filter
          </div>
        </div>
        {/* Scrollable field configuration list */}
        <div className='max-h-[50vh] overflow-y-auto'>
          {fieldConfigs.map((config, index) => (
            <div key={index} className='flex items-center mb-2'>
              {/* Field name input or dropdown */}
              <div
                className={`w-1/3 p-2 mr-2 relative ${config.isNew
                  ? 'border border-gray-300 rounded cursor-pointer'
                  : ''
                  }`}
              >
                <div
                  className={`flex items-center text-[#657584] text-[16px] truncate`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (config.isNew) {
                      const dropdown = document.getElementById(
                        `dropdown-${index}`
                      );
                      if (dropdown)
                        dropdown.style.display =
                          dropdown.style.display === 'block' ? 'none' : 'block';
                    }
                  }}
                >
                  <span
                    className={`flex-grow ${config.name && config.name.length > 14 ? 'tooltip' : ''
                      }`}
                    title={config.name.length > 14 ? config.name : ''}
                  >
                    {config.name && config.name.length > 14
                      ? `${config.name.substring(0, 14)}...`
                      : config.name || 'Select Field'}
                  </span>
                </div>
                {/* Dropdown for new field selection */}
                {config.isNew && (
                  <div
                    id={`dropdown-${index}`}
                    className='absolute z-10 w-full -ml-[8.5px] bg-white border rounded mt-[9px] hidden'
                  >
                    <div className='p-2 border-b'>
                      <input
                        type='text'
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        placeholder='Enter new field name'
                        className='w-full p-1 border rounded mb-1'
                      />
                      <button
                        onClick={() => handleAddNewField(index)}
                        className='w-full px-2 py-1 bg-teal-600 text-white rounded'
                      >
                        Add new field
                      </button>
                    </div>
                    <div>
                      {/* List deleted fields for re-selection */}
                      {deletedFields.map((field) => (
                        <div
                          key={field}
                          className='p-2 hover:bg-gray-100 cursor-pointer'
                          onClick={() => {
                            // setSelectedField(field);
                            updateFieldConfig(index, 'name', field);
                            document.getElementById(
                              `dropdown-${index}`
                            )!.style.display = 'none';
                          }}
                        >
                          {field}
                        </div>
                      ))}
                      {/* List other available fields */}
                      {fields
                        .filter(
                          (field) =>
                            !fieldConfigs.some(
                              (config) => config.name === field
                            ) &&
                            field !== 'Name & Email' &&
                            !deletedFields.includes(field)
                        )
                        .map((field) => (
                          <div
                            key={field}
                            className='p-2 hover:bg-gray-100 cursor-pointer'
                            onClick={() => {
                              // setSelectedField(field);
                              updateFieldConfig(index, 'name', field);
                              document.getElementById(
                                `dropdown-${index}`
                              )!.style.display = 'none';
                            }}
                          >
                            {field}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              {/* Input type selection */}
              <div className='w-1/3 flex items-center'>
                {!config.isNew && (
                  <span className='-ml-[30%] mr-[24%]'>
                    <FaArrowRightComponent />
                  </span>
                )}
                <select
                  value={config.inputType}
                  onChange={(e) =>
                    updateFieldConfig(index, 'inputType', e.target.value)
                  }
                  className={`w-full p-2 border rounded mr-2 ${config.isMandatory ? 'text-[grey]' : ''
                    }`}
                  disabled={config.isMandatory}
                >
                  <option value='Text'>Text</option>
                  <option value='Dropdown'>Dropdown</option>
                  <option value='Number'>Number</option>
                  <option value='Calendar'>Calendar</option>
                </select>
              </div>
              {/* Filter toggle */}
              <select
                disabled={config.isMandatory}
                value={config.filter ? 'Yes' : 'No'}
                onChange={(e) =>
                  updateFieldConfig(index, 'filter', e.target.value === 'Yes')
                }
                className={`w-1/6 p-2 border rounded mr-2 ${config.isMandatory ? 'text-[grey]' : ''
                  }`}
              >
                <option value='Yes'>Yes</option>
                <option value='No'>No</option>
              </select>
              {/* Delete button for non-mandatory fields */}
              {!config.isMandatory && (
                <button
                  onClick={() =>
                    setFieldConfigs(fieldConfigs.filter((_, i) => i !== index))
                  }
                  className='text-red-500 hover:text-red-700 ml-8'
                >
                  <FiXComponent size={20} />
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Action buttons */}
        <div className='flex justify-end mt-4'>
          <button
            onClick={handleClose}
            className='px-4 py-2 bg-gray-200 rounded mr-2'
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 rounded ${hasChanges
              ? 'bg-teal-600 text-white'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            disabled={!hasChanges}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTableFormatModal;
